// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later

import './styles.css';
import { loadCore } from './data';
import { initTooltip } from './tooltip';
import { initGlossary } from './glossary';
import { openAbout } from './about';
import { openRecordDrawer } from './record';
import type { Core, Ctx, Geo, Meta, View } from './types';

import { searchView } from './views/search';
import { whereView } from './views/where';
import { transitionView } from './views/transition';
import { registerView } from './views/register';
import { whatForView } from './views/whatfor';
import { listsView } from './views/lists';
import { leaderboardView } from './views/leaderboard';
import { whoView } from './views/who';

interface Route {
  id: string;
  label: string;
  view: View;
}

const ROUTES: Route[] = [
  { id: 'search', label: 'Search', view: searchView },
  { id: 'where', label: 'Where', view: whereView },
  { id: 'leaderboard', label: 'Leaderboard', view: leaderboardView },
  { id: 'transition', label: 'The transition', view: transitionView },
  { id: 'register', label: 'The register', view: registerView },
  { id: 'whatfor', label: 'What for', view: whatForView },
  { id: 'lists', label: 'The lists', view: listsView },
  { id: 'who', label: 'Who', view: whoView },
];

const app = document.getElementById('app')!;
let teardown: (() => void) | void;

function currentRoute(): { id: string; params: URLSearchParams } {
  const raw = location.hash.replace(/^#\/?/, '');
  const [id, query] = raw.split('?');
  const found = ROUTES.find((r) => r.id === id);
  return { id: found ? id : ROUTES[0].id, params: new URLSearchParams(query ?? '') };
}

function shell(core: Core, meta: Meta): { content: HTMLElement; nav: HTMLElement } {
  app.innerHTML = '';

  const skip = document.createElement('a');
  skip.className = 'skip-link';
  skip.href = '#main';
  skip.textContent = 'Skip to content';

  const header = document.createElement('header');
  header.className = 'site-header';
  const inner = document.createElement('div');
  inner.className = 'header-inner';

  const brand = document.createElement('div');
  brand.className = 'brand';
  brand.innerHTML = `<span class="brand-mark">Honours</span>
    <span class="brand-sub">${meta.records.toLocaleString('en-AU')} Australian awards on the public record</span>`;

  const nav = document.createElement('nav');
  nav.className = 'nav';
  nav.setAttribute('aria-label', 'Views');

  const about = document.createElement('button');
  about.type = 'button';
  about.className = 'icon-btn';
  about.setAttribute('aria-label', 'About this site, its sources and its limits');
  about.textContent = '?';
  about.addEventListener('click', () => openAbout(core, meta));

  inner.append(brand, nav, about);
  header.appendChild(inner);

  const content = document.createElement('main');
  content.className = 'main-content';
  content.id = 'main';
  content.tabIndex = -1;

  const footer = document.createElement('footer');
  footer.className = 'site-footer';
  footer.innerHTML = `<div class="footer-inner">
    <div>Source: the Australian honours register, Department of the Prime Minister and Cabinet —
      <a href="https://honours.pmc.gov.au/honours/search" target="_blank" rel="noopener">honours.pmc.gov.au</a>.
      Honours data © Commonwealth of Australia 2023, licensed
      <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener">CC BY 4.0</a>.
      Population, income and boundaries © Australian Bureau of Statistics (2021 Census), CC BY 4.0.</div>
    <div>Records here are a snapshot. To correct or remove an entry, contact the Australian
      Honours and Awards Secretariat at <a href="mailto:honours@gg.gov.au">honours@gg.gov.au</a>.
      This site is not affiliated with the Australian Government.</div>
    <div>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> ·
      <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></div>
  </div>`;

  app.append(skip, header, content, footer);
  return { content, nav };
}

function render(core: Core, geo: Geo, meta: Meta, content: HTMLElement, nav: HTMLElement): void {
  const { id, params } = currentRoute();
  const route = ROUTES.find((r) => r.id === id)!;

  nav.innerHTML = '';
  for (const r of ROUTES) {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = r.label;
    if (r.id === id) b.setAttribute('aria-current', 'page');
    b.addEventListener('click', () => {
      location.hash = `#/${r.id}`;
    });
    nav.appendChild(b);
  }

  if (typeof teardown === 'function') teardown();
  content.innerHTML = '';

  const ctx: Ctx = {
    core,
    geo,
    meta,
    go(target, p) {
      const q = p ? `?${new URLSearchParams(p).toString()}` : '';
      location.hash = `#/${target}${q}`;
    },
    openRecord(rec) {
      openRecordDrawer(rec, core);
    },
  };

  // Views read their own state from the hash query string.
  (content as HTMLElement & { dataset: DOMStringMap }).dataset.params = params.toString();

  try {
    teardown = route.view(content, ctx);
  } catch (err) {
    content.innerHTML = `<div class="error" role="alert">
      <p>Something went wrong drawing this view.</p>
      <p class="panel-sub">${(err as Error).message}</p></div>`;
  }
  content.focus({ preventScroll: true });
  window.scrollTo({ top: 0 });
}

async function boot(): Promise<void> {
  app.innerHTML = `<div class="loading">Loading the honours register…</div>`;
  initTooltip();
  initGlossary();

  let data: { core: Core; geo: Geo; meta: Meta };
  try {
    data = await loadCore();
  } catch (err) {
    app.innerHTML = `<div class="error" role="alert">
      <p>Could not load the honours data.</p>
      <p class="panel-sub">${(err as Error).message}</p>
      <p><button type="button" class="pill" onclick="location.reload()">Try again</button></p>
    </div>`;
    return;
  }

  const { core, geo, meta } = data;
  const { content, nav } = shell(core, meta);
  const draw = (): void => render(core, geo, meta, content, nav);
  window.addEventListener('hashchange', draw);
  draw();
}

void boot();
